import { LocaleDate } from '@mi-front/ui';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

/**
 * Returns true when the date (local calendar day) is today.
 */
function isToday(dt: DateTime): boolean {
  return dt.hasSame(DateTime.now(), 'day');
}

/**
 * Returns true when the date (local calendar day) is yesterday.
 */
function isYesterday(dt: DateTime): boolean {
  return dt.hasSame(DateTime.now().minus({ days: 1 }), 'day');
}

/**
 * Wraps label + time in a single inline-flex row. Used to avoid duplicating
 * the same Box structure for today/yesterday and full-date variants.
 */
function TimeWithLabel({ gap = 0.5, children }: { gap?: number; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap }}>
      {children}
    </Box>
  );
}

/** Shared time segment (HH:mm) used in all date variants. */
function formatTimeOnly(from: string) {
  return <FormattedTime value={new Date(from)} hour="2-digit" minute="2-digit" hour12={false} />;
}

/**
 * Fragment start time: HH:mm if today/yesterday, else DD/MM HH:mm.
 * Renders content only (for embedding in another pill or container).
 */
export function FragmentCardTime({
  from,
  showRecentDate = false,
  dateFormat = 'short',
}: {
  from: string;
  showRecentDate?: boolean;
  dateFormat?: 'short' | 'long';
}) {
  const fromDt = DateTime.fromISO(from);
  const isRecent = isToday(fromDt) || isYesterday(fromDt);
  const showDate = !isRecent || (showRecentDate && isRecent);

  const timePart = formatTimeOnly(from);

  if (showDate) {
    let datePart: ReactNode;

    if (isToday(fromDt)) {
      datePart = <FormattedMessage id="fragments.listV3.section.today" defaultMessage="Hoy" />;
    }

    if (isYesterday(fromDt)) {
      datePart = <FormattedMessage id="fragments.listV3.section.yesterday" defaultMessage="Ayer" />;
    } else {
      datePart = (
        <>
          <Box component="span" sx={{ textTransform: 'capitalize' }}>
            <FormattedDate value={new Date(from)} weekday={dateFormat} />
          </Box>{' '}
          <LocaleDate value={from} day="2-digit" month="2-digit" />
        </>
      );
    }

    return (
      <TimeWithLabel gap={0.75}>
        <span>{datePart}</span>
        <span>{timePart}</span>
      </TimeWithLabel>
    );
  }

  return timePart;
}
